import { Switch, Route, useLocation } from "wouter";
import { Suspense, lazy, useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth";
import { SidebarProvider } from "@/lib/sidebar-context";

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Structural layouts — imported directly (not lazy) so they share the same
// module instance as AuthProvider and never trigger the useAuth context error.
import ShopLayout from "@/components/shop/ShopLayout";
import AdminLayout from "@/components/admin/AdminLayout";

// Public pages
const NotFound = lazy(() => import("@/pages/not-found"));
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const RegisterShop = lazy(() => import("@/pages/RegisterShop"));
const ForShops = lazy(() => import("@/pages/ForShops"));
const ShopLogin = lazy(() => import("@/pages/ShopLogin"));
const ShopRegister = lazy(() => import("@/pages/ShopRegister"));
const BookRepair = lazy(() => import("@/pages/BookRepair"));
const TrackRepair = lazy(() => import("@/pages/TrackRepair"));
const ShopProfile = lazy(() => import("@/pages/ShopProfile"));
const MyRepairs = lazy(() => import("@/pages/MyRepairs"));
const MyWarranties = lazy(() => import("@/pages/MyWarranties"));
const WarrantyLookup = lazy(() => import("@/pages/WarrantyLookup"));
const Widget = lazy(() => import("@/pages/Widget"));
const LeaveReview = lazy(() => import("@/pages/LeaveReview"));
const TwoFactorSetup = lazy(() => import("@/pages/TwoFactorSetup"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Sms = lazy(() => import("@/pages/Sms"));
const Support = lazy(() => import("@/pages/Support"));
const ScanPage = lazy(() => import("@/pages/Scan"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const PublicTradeIn = lazy(() => import("@/pages/PublicTradeIn"));
const ApproveEstimate = lazy(() => import("@/pages/ApproveEstimate"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const ClaimShop = lazy(() => import("@/pages/ClaimShop"));
const Kiosk = lazy(() => import("@/pages/Kiosk"));
const Queue = lazy(() => import("@/pages/Queue"));
const CustomerDisplay = lazy(() => import("@/pages/CustomerDisplay"));
const EstimateApproval = lazy(() => import("@/pages/EstimateApproval"));
const ApproveWork = lazy(() => import("@/pages/ApproveWork"));
const QueueDisplay = lazy(() => import("@/pages/QueueDisplay"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Directory = lazy(() => import("@/pages/Directory"));
const RepairStatePage = lazy(() => import("@/pages/RepairStatePage"));
const RepairDevicePage = lazy(() => import("@/pages/RepairDevicePage"));

// Shop dashboard pages
const ShopDashboard = lazy(() => import("@/pages/shop/Dashboard"));
const Tickets = lazy(() => import("@/pages/shop/Tickets"));
const TicketDetail = lazy(() => import("@/pages/shop/TicketDetail"));
const KanbanBoard = lazy(() => import("@/pages/shop/KanbanBoard"));
const CheckIn = lazy(() => import("@/pages/shop/CheckIn"));
const Customers = lazy(() => import("@/pages/shop/Customers"));
const CustomerDetail = lazy(() => import("@/pages/shop/CustomerDetail"));
const NewCustomer = lazy(() => import("@/pages/shop/NewCustomer"));
const Inventory = lazy(() => import("@/pages/shop/Inventory"));
const POS = lazy(() => import("@/pages/shop/POS"));
const Templates = lazy(() => import("@/pages/shop/Templates"));
const Analytics = lazy(() => import("@/pages/shop/Analytics"));
const ShopReviews = lazy(() => import("@/pages/shop/Reviews"));
const Loyalty = lazy(() => import("@/pages/shop/Loyalty"));
const Staff = lazy(() => import("@/pages/shop/Staff"));
const StaffSchedule = lazy(() => import("@/pages/shop/StaffSchedule"));
const EndOfDayReport = lazy(() => import("@/pages/shop/EndOfDayReport"));
const ShopSettings = lazy(() => import("@/pages/shop/Settings"));
const ShopVendors = lazy(() => import("@/pages/shop/Vendors"));
const DeviceManager = lazy(() => import("@/pages/shop/DeviceManager"));
const Invoices = lazy(() => import("@/pages/shop/Invoices"));
const Estimates = lazy(() => import("@/pages/shop/Estimates"));
const ShopDeviceCatalog = lazy(() => import("@/pages/shop/DeviceCatalog"));
const ServicePricing = lazy(() => import("@/pages/shop/ServicePricing"));
const QuickPrice = lazy(() => import("@/pages/shop/QuickPrice"));
const MultiLocation = lazy(() => import("@/pages/shop/MultiLocation"));
const HQDashboard = lazy(() => import("@/pages/shop/HQDashboard"));
const InventoryTransfers = lazy(() => import("@/pages/shop/InventoryTransfers"));
const PurchaseOrders = lazy(() => import("@/pages/shop/PurchaseOrders"));
const Appointments = lazy(() => import("@/pages/shop/Appointments"));
const TradeIns = lazy(() => import("@/pages/shop/TradeIns"));
const ShopReferrals = lazy(() => import("@/pages/shop/Referrals"));
const VoiceAssistant = lazy(() => import("@/pages/shop/VoiceAssistant"));
const ShopBilling = lazy(() => import("@/pages/shop/Billing"));
const ImportData = lazy(() => import("@/pages/shop/ImportData"));
const ApiDocs = lazy(() => import("@/pages/shop/ApiDocs"));
const SmsOutreach = lazy(() => import("@/pages/shop/SmsOutreach"));
const Coupons = lazy(() => import("@/pages/shop/Coupons"));
const MyQueue = lazy(() => import("@/pages/shop/MyQueue"));
const ShopSupport = lazy(() => import("@/pages/shop/ShopSupport"));
const ShopHelp = lazy(() => import("@/pages/shop/ShopHelp"));
const Expenses = lazy(() => import("@/pages/shop/Expenses"));
const LoanerDevices = lazy(() => import("@/pages/shop/LoanerDevices"));
const SmsInbox = lazy(() => import("@/pages/shop/SmsInbox"));
const ProtectionPlans = lazy(() => import("@/pages/shop/ProtectionPlans"));
const RepairGuides = lazy(() => import("@/pages/shop/Guides"));

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminContactRequests = lazy(() => import("@/pages/admin/ContactRequests"));
const AdminShops = lazy(() => import("@/pages/admin/Shops"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/Subscriptions"));
const AdminDeviceCatalog = lazy(() => import("@/pages/admin/DeviceCatalog"));
const AdminStorageSettings = lazy(() => import("@/pages/admin/StorageSettings"));
const AdminIntegrations = lazy(() => import("@/pages/admin/Integrations"));
const AdminSeoSettings = lazy(() => import("@/pages/admin/SeoSettings"));
const AdminReservedUsernames = lazy(() => import("@/pages/admin/ReservedUsernames"));
const AdminOutreach = lazy(() => import("@/pages/admin/Outreach"));
const AdminShopDiscovery = lazy(() => import("@/pages/admin/ShopDiscovery"));
const AdminPhoneNumbers = lazy(() => import("@/pages/admin/PhoneNumbers"));
const AdminAddonPackages = lazy(() => import("@/pages/admin/AddonPackages"));
const AdminReferrals = lazy(() => import("@/pages/admin/Referrals"));
const AdminBlog = lazy(() => import("@/pages/admin/Blog"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/EmailTemplates"));
const AdminSupportTickets = lazy(() => import("@/pages/admin/SupportTickets"));
const AdminUserGuide = lazy(() => import("@/pages/admin/UserGuide"));
const AdminDirectory = lazy(() => import("@/pages/admin/DirectoryAdmin"));

const SHOP_ROLES = ["shop_owner", "technician", "manager", "front_desk", "admin"];

function ShopRoutes() {
  const { user, isLoading } = useAuth();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!user) navigate("/shop-login");
    else if (!SHOP_ROLES.includes(user.role)) navigate("/shop-register");
  }, [isLoading, user]);

  if (isLoading || !user || !SHOP_ROLES.includes(user.role)) return null;

  return (
    <SidebarProvider>
      <ShopLayout>
        <Suspense key={location} fallback={<PageLoader />}>
          <Switch>
            <Route path="/shop" component={ShopDashboard} />
            <Route path="/shop/tickets/:id" component={TicketDetail} />
            <Route path="/shop/tickets" component={Tickets} />
            <Route path="/shop/kanban" component={KanbanBoard} />
            <Route path="/shop/check-in" component={CheckIn} />
            <Route path="/shop/customers/new" component={NewCustomer} />
            <Route path="/shop/customers/:id" component={CustomerDetail} />
            <Route path="/shop/customers" component={Customers} />
            <Route path="/shop/inventory" component={Inventory} />
            <Route path="/shop/pos" component={POS} />
            <Route path="/shop/voice-assistant" component={VoiceAssistant} />
            <Route path="/shop/templates" component={Templates} />
            <Route path="/shop/analytics" component={Analytics} />
            <Route path="/shop/eod-report" component={EndOfDayReport} />
            <Route path="/shop/staff-schedule" component={StaffSchedule} />
            <Route path="/shop/staff" component={Staff} />
            <Route path="/shop/reviews" component={ShopReviews} />
            <Route path="/shop/loyalty" component={Loyalty} />
            <Route path="/shop/vendors" component={ShopVendors} />
            <Route path="/shop/purchase-orders" component={PurchaseOrders} />
            <Route path="/shop/appointments" component={Appointments} />
            <Route path="/shop/trade-ins" component={TradeIns} />
            <Route path="/shop/invoices" component={Invoices} />
            <Route path="/shop/estimates" component={Estimates} />
            <Route path="/shop/quick-price" component={QuickPrice} />
            <Route path="/shop/catalog" component={ShopDeviceCatalog} />
            <Route path="/shop/pricing" component={ServicePricing} />
            <Route path="/shop/devices" component={DeviceManager} />
            <Route path="/shop/billing" component={ShopBilling} />
            <Route path="/shop/import" component={ImportData} />
            <Route path="/shop/referrals" component={ShopReferrals} />
            <Route path="/shop/api-docs" component={ApiDocs} />
            <Route path="/shop/sms-outreach" component={SmsOutreach} />
            <Route path="/shop/coupons" component={Coupons} />
            <Route path="/shop/my-queue" component={MyQueue} />
            <Route path="/shop/support" component={ShopSupport} />
            <Route path="/shop/help" component={ShopHelp} />
            <Route path="/shop/settings" component={ShopSettings} />
            <Route path="/shop/multi-location" component={MultiLocation} />
            <Route path="/shop/transfers" component={InventoryTransfers} />
            <Route path="/shop/hq/:groupId" component={HQDashboard} />
            <Route path="/shop/expenses" component={Expenses} />
            <Route path="/shop/loaner-devices" component={LoanerDevices} />
            <Route path="/shop/sms-inbox" component={SmsInbox} />
            <Route path="/shop/protection-plans" component={ProtectionPlans} />
            <Route path="/shop/guides" component={RepairGuides} />
            <Route component={ShopDashboard} />
          </Switch>
        </Suspense>
      </ShopLayout>
    </SidebarProvider>
  );
}

function AdminRoutes() {
  const { user, isLoading } = useAuth();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      navigate("/login");
    } else if (user.role !== "admin") {
      navigate("/shop");
    }
  }, [isLoading, user]);

  if (isLoading || !user || user.role !== "admin") return null;

  return (
    <AdminLayout>
      <Suspense key={location} fallback={<PageLoader />}>
        <Switch>
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/shops" component={AdminShops} />
          <Route path="/admin/contact-requests" component={AdminContactRequests} />
          <Route path="/admin/subscriptions" component={AdminSubscriptions} />
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin/reviews" component={AdminReviews} />
          <Route path="/admin/catalog" component={AdminDeviceCatalog} />
          <Route path="/admin/storage" component={AdminStorageSettings} />
          <Route path="/admin/integrations" component={AdminIntegrations} />
          <Route path="/admin/seo" component={AdminSeoSettings} />
          <Route path="/admin/reserved-usernames" component={AdminReservedUsernames} />
          <Route path="/admin/shop-discovery" component={AdminShopDiscovery} />
          <Route path="/admin/outreach" component={AdminOutreach} />
          <Route path="/admin/phone-numbers" component={AdminPhoneNumbers} />
          <Route path="/admin/addon-packages" component={AdminAddonPackages} />
          <Route path="/admin/referrals" component={AdminReferrals} />
          <Route path="/admin/blog" component={AdminBlog} />
          <Route path="/admin/email-templates" component={AdminEmailTemplates} />
          <Route path="/admin/support-tickets" component={AdminSupportTickets} />
          <Route path="/admin/user-guide" component={AdminUserGuide} />
          <Route path="/admin/directory" component={AdminDirectory} />
          <Route component={AdminDashboard} />
        </Switch>
      </Suspense>
    </AdminLayout>
  );
}

function HomeRoute() {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  if (hostname === "shop.ifixable.com") return <ForShops />;
  if (hostname === "app.ifixable.com") return <ShopLogin />;
  return <Landing />;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={HomeRoute} />
        <Route path="/landing" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/for-shops" component={ForShops} />
        <Route path="/shop-login" component={ShopLogin} />
        <Route path="/shop-register" component={ShopRegister} />
        <Route path="/register-shop" component={RegisterShop} />
        <Route path="/book" component={BookRepair} />
        <Route path="/invite/:token" component={InviteAccept} />
        <Route path="/claim/:token" component={ClaimShop} />
        <Route path="/track/:ticketNumber" component={TrackRepair} />
        <Route path="/track" component={TrackRepair} />
        <Route path="/my-repairs" component={MyRepairs} />
        <Route path="/my-warranties" component={MyWarranties} />
        <Route path="/review/:ticketId" component={LeaveReview} />
        <Route path="/warranty" component={WarrantyLookup} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/blog" component={Blog} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/contact" component={Contact} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/sms" component={Sms} />
        <Route path="/support" component={Support} />
        <Route path="/2fa-setup" component={TwoFactorSetup} />
        <Route path="/shops/:shopId" component={ShopProfile} />
        <Route path="/widget/:shopId" component={Widget} />
        <Route path="/kiosk/:shopId" component={Kiosk} />
        <Route path="/queue/:shopId" component={Queue} />
        <Route path="/display/:shopId" component={CustomerDisplay} />
        <Route path="/queue-display/:shopId" component={QueueDisplay} />
        <Route path="/scan" component={ScanPage} />
        <Route path="/portal/:shopId" component={CustomerPortal} />
        <Route path="/trade-in/:shopId" component={PublicTradeIn} />
        <Route path="/approve-estimate/:token" component={ApproveEstimate} />
        <Route path="/estimate/:token" component={EstimateApproval} />
        <Route path="/approve-work/:token" component={ApproveWork} />
        <Route path="/directory" component={Directory} />

        <Route path="/repair/state/:stateSlug" component={RepairStatePage} />
        <Route path="/repair/:deviceCategory" component={RepairDevicePage} />

        <Route path="/shop/*" component={ShopRoutes} />
        <Route path="/shop" component={ShopRoutes} />

        <Route path="/admin/*" component={AdminRoutes} />
        <Route path="/admin" component={AdminRoutes} />

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function GoogleAnalytics() {
  const [location] = useLocation();
  const { data } = useQuery<{ googleAnalyticsId: string | null }>({
    queryKey: ["/api/public/analytics-config"],
    staleTime: 5 * 60 * 1000,
  });

  const gaId = data?.googleAnalyticsId;

  useEffect(() => {
    if (!gaId) return;

    if (!document.getElementById("ga-script")) {
      const s = document.createElement("script");
      s.id = "ga-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);

      const init = document.createElement("script");
      init.id = "ga-init";
      init.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}',{send_page_view:false});`;
      document.head.appendChild(init);
    }
  }, [gaId]);

  useEffect(() => {
    if (!gaId || typeof (window as any).gtag !== "function") return;
    (window as any).gtag("event", "page_view", {
      page_path: location,
      send_to: gaId,
    });
  }, [location, gaId]);

  return null;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; retries: number }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null, retries: 0 };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // NotFoundError / removeChild is a transient React 18 concurrent-mode +
    // Suspense timing error: React tries to remove a Suspense fallback node
    // while a TanStack Query state update is committing simultaneously.
    // The next render always succeeds, so auto-recover up to 3 times silently.
    const isTransient =
      error instanceof DOMException ||
      error.name === "NotFoundError" ||
      (error.message ?? "").toLowerCase().includes("removechild") ||
      (error.message ?? "").toLowerCase().includes("insertbefore");

    if (isTransient && this.state.retries < 3) {
      this.setState(s => ({ error: null, retries: s.retries + 1 }));
      return;
    }
    console.error("App error boundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
          <p className="text-lg font-medium">Something went wrong.</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            {this.state.error.message}
          </p>
          <button
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md"
            onClick={() => { this.setState({ error: null, retries: 0 }); window.location.reload(); }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <Toaster />
      <GoogleAnalytics />
      <Router />
    </ErrorBoundary>
  );
}

export default App;
